<template>
    <div ref="toolbar" class="pdf-toolbar" :class="{ 'is-compact': isCompact }">
        <div class="toolbar-left">
            <h4 class="text-truncate">
                {{ title }}
            </h4>
        </div>

        <div class="toolbar-center">
            <input
                type="number"
                class="number-input page-number-input"
                :style="{
                    '--total-pages-digits': totalPagesDigits,
                }"
                :value="currentPage"
                :min="1"
                :max="totalPages || 1"
                :disabled="disableButtons"
                @blur="handlePageInputBlur"
                @keydown.enter="handlePageInputEnter"
            />
            <span class="page-total">/ {{ totalPages || 1 }}</span>
            <span class="separator">|</span>

            <!-- Scale controls -->
            <button
                class="icon-btn icon-btn-white"
                @click="$emit('scale-change', 'decrease')"
                :disabled="disableButtons || scale <= 0.1"
                title="Zoom out"
            >
                <span class="icon-minus"></span>
            </button>

            <input
                type="text"
                class="number-input scale-size-input"
                :value="scalePercentageDisplay"
                @blur="handleScaleInputBlur"
                @keydown.enter="$event.target.blur()"
                title="Scale percentage"
            />

            <button
                class="icon-btn icon-btn-white"
                @click="$emit('scale-change', 'increase')"
                :disabled="disableButtons || scale >= 5.0"
                title="Zoom in"
            >
                <span class="icon-plus"></span>
            </button>
            <span class="separator">|</span>

            <!-- Fit controls -->
            <button
                class="icon-btn icon-btn-white"
                @click="handleFitChange"
                :disabled="disableButtons"
                :title="fitMode === 'height' ? 'Fit to width' : 'Fit to height'"
            >
                <span v-if="fitMode === 'height'" class="icon-fit-width"></span>
                <span v-else class="icon-fit-height"></span>
            </button>
            <button
                class="icon-btn icon-btn-white"
                @click="$emit('rotate-page')"
                title="Rotate page"
                :disabled="disableButtons"
            >
                <span class="icon-rotate"></span>
            </button>
        </div>

        <div class="toolbar-right">
            <ToolTip>
                <template #tooltip-trigger>
                    <button
                        class="icon-btn icon-btn-white"
                        @click="$emit('download-pdf')"
                        title="Download PDF"
                        :disabled="
                            disableButtons ||
                            disableDownloadButton ||
                            downloading
                        "
                    >
                        <i class="fi fi-download"></i>
                    </button>
                </template>
                <template #tooltip-content v-if="disableDownloadButton">
                    You do not have permission to download this PDF.
                </template>
            </ToolTip>
            <ToolTip>
                <template #tooltip-trigger>
                    <button
                        class="icon-btn icon-btn-white"
                        @click="$emit('print-pdf')"
                        title="Print PDF"
                        :disabled="
                            disableButtons ||
                            disableDownloadButton ||
                            printerLoading
                        "
                    >
                        <span class="icon-print"></span>
                    </button>
                </template>
                <template #tooltip-content v-if="disableDownloadButton">
                    You do not have permission to print this PDF.
                </template>
            </ToolTip>
        </div>
    </div>
</template>

<script>
import ToolTip from '@/components/fields/ToolTip.vue';

export default {
    name: 'PdfToolbar',
    components: {
        ToolTip,
    },
    props: {
        title: { type: String, default: 'PDF Document' },
        currentPage: { type: Number, default: 1 },
        totalPages: { type: Number, default: 1 },
        scale: { type: Number, default: 1.0 },
        fitMode: { type: String, default: null },
        isLoading: { type: Boolean, default: false },
        error: { type: String, default: null },
        disableDownloadButton: { type: Boolean, default: false },
        printerLoading: { type: Boolean, default: false },
        downloading: { type: Boolean, default: false },
    },
    emits: [
        'page-change',
        'scale-change', 
        'fit-change',
        'rotate-page',
        'download-pdf',
        'print-pdf',
    ],
    data() {
        return {
            isCompact: false,
            resizeObserver: null,
        };
    },
    computed: {
        scalePercentageDisplay() {
            const percentage = Math.round(this.scale * 100);
            return percentage + '%';
        },

        totalPagesDigits() {
            return this.totalPages?.toString().length;
        },

        disableButtons() {
            return this.isLoading || this.error;
        },
    },
    methods: {
        updateCompact() {
            const width =
                this.$refs.toolbar?.getBoundingClientRect().width || 0;
            this.isCompact = width <= 760;
        },

        handlePageInputEnter(event) {
            event.target.blur();
        },

        handlePageInputBlur(event) {
            const value = parseInt(event.target.value);
            if (value >= 1 && value <= this.totalPages) {
                this.$emit('page-change', value);
            } else {
                event.target.value = this.currentPage;
            }
        },

        handleScaleInputBlur(event) {
            let value = event.target.value.replace('%', '').trim();
            const numericValue = parseInt(value);

            if (numericValue >= 10 && numericValue <= 500) {
                // Allow up to 500%
                this.$emit('scale-change', numericValue / 100);
            } else {
                // Reset to current value if invalid
                event.target.value = this.scalePercentageDisplay;
            }
        },

        handleFitChange() {
            if (this.fitMode === 'width') {
                this.$emit('fit-change', 'height');
            } else if (this.fitMode === 'height') {
                this.$emit('fit-change', 'width');
            } else {
                this.$emit('fit-change', 'height');
            }
        },
    },
    mounted() {
        if (typeof ResizeObserver !== 'undefined') {
            this.resizeObserver = new ResizeObserver(() => {
                this.updateCompact();
            });
            this.resizeObserver.observe(this.$refs.toolbar);
            this.updateCompact();
        }
    },
    beforeUnmount() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    },
};
</script>

<style scoped>
.pdf-toolbar {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    align-items: center;
    width: 100%;
    min-height: var(--pdf-toolbar-height);
    gap: var(--space-4);

    background-color: #f9f9fa;
    border-bottom: 1px solid var(--neutral-600);

    padding-inline: var(--space-4);
    padding-block: var(--space-2);
}

.toolbar-left,
.toolbar-center,
.toolbar-right {
    display: inline-flex;
    align-items: center;
    gap: var(--space-2);
    min-width: 0;
}

.toolbar-left {
    justify-self: start;
    overflow: hidden;
    max-width: 100%;
}
.toolbar-center {
    justify-self: center;
}
.toolbar-right {
    justify-self: end;
}

.toolbar-left h4 {
    color: var(--neutral-900);
    font-size: 0.875rem;
    font-weight: 500;
    margin: 0;
    max-width: 100%;
}

/* inputs */
.number-input {
    color: var(--neutral-900);
    font-size: 0.95rem;
    border: 1px solid var(--neutral-600);
    background-color: var(--white);
    border-radius: var(--radius-default);
    text-align: center;
    outline: none;
    padding: var(--size-1) var(--size-2);
    height: var(--size-8);
    appearance: textfield;
    -moz-appearance: textfield;
    -webkit-appearance: textfield;
}

.number-input::-webkit-outer-spin-button,
.number-input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
}

.page-number-input {
    width: calc(max(2, var(--total-pages-digits, 2)) * 1ch + 1.125rem);
}

.scale-size-input {
    width: 4rem;
}

.page-total {
    color: var(--neutral-800);
    font-size: 0.95rem;
}

.separator {
    color: var(--neutral-600);
    font-size: var(--size-5);
    line-height: 1;
}

.icon-btn:hover span {
    background-color: var(--white);
}

.icon-btn:disabled span {
    background-color: var(--neutral-800);
}

/* icons */
.pdf-toolbar .icon-plus,
.pdf-toolbar .icon-minus,
.pdf-toolbar .icon-fit-width,
.pdf-toolbar .icon-fit-height,
.pdf-toolbar .icon-rotate,
.pdf-toolbar .icon-print {
    display: inline-block;
    width: 1.125rem;
    height: 1.125rem;
    background-color: var(--neutral-900);
    mask-size: 100% 100%;
    mask-repeat: no-repeat;
}

.icon-plus {
    mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath fill='black' d='M13.2 1.2h-2.4v9.6H1.2v2.4h9.6v9.6h2.4v-9.6h9.6v-2.4h-9.6z'/%3E%3C/svg%3E");
}
.icon-minus {
    mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath fill='black' d='M22.8 13.2v-2.4H1.2v2.4h12z'/%3E%3C/svg%3E");
}
.icon-fit-width {
    mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 32 32'%3E%3Cpath fill='black' d='m22 11l-1.41 1.41L23.17 15H8.83l2.58-2.59L10 11l-5 5l5 5l1.41-1.41L8.83 17h14.34l-2.58 2.59L22 21l5-5z'/%3E%3Cpath fill='black' d='M28 30H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h24a2 2 0 0 1 2 2v24a2 2 0 0 1-2 2M4 4v24h24V4Z'/%3E%3C/svg%3E");
}
.icon-fit-height {
    mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 32 32'%3E%3Cpath fill='black' d='m11 10l1.41 1.41L15 8.83v14.34l-2.59-2.58L11 22l5 5l5-5l-1.41-1.41L17 23.17V8.83l2.59 2.58L21 10l-5-5z'/%3E%3Cpath fill='black' d='M28 30H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h24a2 2 0 0 1 2 2v24a2 2 0 0 1-2 2M4 4v24h24V4Z'/%3E%3C/svg%3E");
}
.icon-rotate {
    mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 512 512'%3E%3Cpath fill='black' d='M248.91 50a206 206 0 0 1 35.857 3.13c85.207 15.025 152.077 81.895 167.102 167.102c15.023 85.208-24.944 170.917-99.874 214.178c-32.782 18.927-69.254 27.996-105.463 27.553c-46.555-.57-92.675-16.865-129.957-48.15l30.855-36.768a157.85 157.85 0 0 0 180.566 15.797a157.85 157.85 0 0 0 76.603-164.274A157.85 157.85 0 0 0 276.429 100.4a157.84 157.84 0 0 0-139.17 43.862L185 192H57V64l46.34 46.342C141.758 71.962 194.17 50.03 248.91 50'/%3E%3C/svg%3E");
}
.icon-print {
    mask: url("data:image/svg+xml,%3Csvg width='18' height='18' viewBox='0 0 18 18' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cg clip-path='url(%23clip0_672_37)'%3E%3Cpath d='M4.5 6.75V1.5H13.5V6.75M4.5 13.5H3C2.60218 13.5 2.22064 13.342 1.93934 13.0607C1.65804 12.7794 1.5 12.3978 1.5 12V8.25C1.5 7.85218 1.65804 7.47064 1.93934 7.18934C2.22064 6.90804 2.60218 6.75 3 6.75H15C15.3978 6.75 15.7794 6.90804 16.0607 7.18934C16.342 7.47064 16.5 7.85218 16.5 8.25V12C16.5 12.3978 16.342 12.7794 16.0607 13.0607C15.7794 13.342 15.3978 13.5 15 13.5H13.5M4.5 10.5H13.5V16.5H4.5V10.5Z' stroke='black' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/g%3E%3Cdefs%3E%3CclipPath id='clip0_672_37'%3E%3Crect width='18' height='18' fill='white'/%3E%3C/clipPath%3E%3C/defs%3E%3C/svg%3E");
}

.pdf-toolbar.is-compact {
    display: grid;
    grid-template-columns: 1fr auto;
    grid-template-areas: 'center end';
}

.pdf-toolbar.is-compact .toolbar-left {
    display: none;
}

.pdf-toolbar.is-compact .toolbar-center {
    grid-column: 1;
    justify-self: start;
    gap: var(--space-1);
}

.pdf-toolbar.is-compact .toolbar-right {
    grid-column: 2;
    justify-self: end;
    gap: var(--space-1);
}
.pdf-toolbar.is-compact .toolbar-left h4 {
    display: none;
}
.pdf-toolbar.is-compact .scale-size-input {
    display: none;
}
</style>
